import { useState, type FormEvent } from "react";

import { ApiError } from "../api/errors";
import { useAuth } from "./AuthProvider";
import "./login.css";

/**
 * An administrator replaces their own password.
 *
 * TWO ARRIVALS, ONE SCREEN. A new account is sent here by `RequireAuth` because
 * it is holding a password an owner generated and the backend refuses it every
 * business route until that changes; anybody else may come here from the header
 * because they want to. The only difference is the sentence at the top — the
 * form, the rules and the consequences are identical, and a second screen would
 * be a second place to get them wrong.
 *
 * IT ENDS THE SESSION, AND SAYS SO BEFORE THE BUTTON IS PRESSED. The server
 * revokes every session for the account, including this browser's, and clears
 * the cookies. That is not a limitation to be worked around: a password is
 * changed when it may have been seen, and leaving the browser that changed it
 * signed in leaves a session issued against a credential nobody trusts any
 * more. So the operator is told, and is then sent to the login screen — where
 * signing in with the new password is also the proof that it took.
 *
 * NOTHING IS REMEMBERED. Neither password is put anywhere but this component's
 * state, and both are gone the moment it unmounts.
 */
export function ChangePasswordPage() {
  const { changePassword, mustChangePassword, email, signOut } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Checked here only so the operator is not made to wait for a round trip to
  // learn they mistyped the same thing twice. Every rule that MATTERS — the
  // length floor, that the new one differs from the old, that the old one is
  // right — is the server's, and this screen shows whatever it answers.
  const mismatch = confirm.length > 0 && next !== confirm;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      // Nothing to navigate to: the session this screen was using has just
      // been revoked, so `RequireAuth` sends the browser to the login page on
      // its own, and the provider carries the one fact that screen needs.
      await changePassword(current, next);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.userMessage);
      } else {
        setError("The password could not be changed.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login__card panel" onSubmit={onSubmit}>
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true" />
          <h1 className="login__title">
            {mustChangePassword ? "Choose your password" : "Change password"}
          </h1>
        </div>

        {mustChangePassword ? (
          <p className="login__lead muted" data-testid="forced-password-notice">
            Your account is using a temporary password an owner created for you.
            Choose your own before you can use the dashboard.
          </p>
        ) : (
          <p className="login__lead muted">
            Signed in as {email ?? "this account"}.
          </p>
        )}

        {error ? (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        ) : null}

        <div>
          <label htmlFor="current-password">Current password</label>
          <input
            id="current-password"
            name="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={busy}
          />
        </div>

        <div>
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={busy}
          />
        </div>

        <div>
          <label htmlFor="confirm-password">Repeat new password</label>
          <input
            id="confirm-password"
            name="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
          />
          {mismatch ? (
            <p className="field-error">The two new passwords do not match.</p>
          ) : null}
        </div>

        <p className="muted">
          Changing your password signs you out everywhere, including this
          browser. You will be asked to sign in again with the new one.
        </p>

        <button
          type="submit"
          className="btn btn--primary login__submit"
          disabled={busy || mismatch}
        >
          {busy ? "Changing…" : "Change password"}
        </button>

        {/* The way out for somebody who arrived here by mistake — and the only
            other thing an account with a temporary password may do. */}
        <button
          type="button"
          className="btn btn--subtle"
          disabled={busy}
          onClick={() => void signOut()}
        >
          Log out
        </button>
      </form>
    </main>
  );
}
